import React, {useState, useEffect} from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import {useTranslation} from 'react-i18next';

import {isTablet, useIsMobile, webMediaQuery} from '../../../styles';
import {ReactComponent as ArrowIcon} from '../../../assets/arrow.svg';

import styled from 'styled-components';
import ChapterRow from '../ChapterRow';
import {
    ButtonNext,
    ButtonPrev,
    LeftControls,
    RightControls,
} from '../../GamesCarousel/styles';

function ChapterCarousel({items, handleClickContent, ...props}) {
    const scrollContainerRef = React.useRef();
    const isMobile = useIsMobile();
    const {t} = useTranslation();

    const [currentSlide, setCurrentSlide] = useState(0);
    const [visibleSlides, setVisibleSlides] = useState(4);

    const screenWidth = window.innerWidth > 1440 ? 1440 : window.innerWidth;
    const margin = isMobile ? 50 : 80;
    let columnQuantity = 4;
    let itemColumns = 1;

    if (window.innerWidth > 1024) {
        columnQuantity = 4;
    } else if (window.innerWidth > 768) {
        columnQuantity = 11;
        itemColumns = 4;
    } else if (window.innerWidth > 600) {
        columnQuantity = 9;
        itemColumns = 4;
    } else {
        columnQuantity = 1;
    }

    const [columnSize, setColumnSize] = useState(
        (screenWidth - margin) / columnQuantity,
    );
    const [slideWidth, setSlideWidth] = useState(itemColumns * columnSize);
    const [gridWidth, setGridWidth] = useState(
        slideWidth * items.length - margin / 2,
    );

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        handleWindowResize();

        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line
    }, [screenWidth]);

    const handleWindowResize = () => {
        if (scrollContainerRef.current) {
            if (!isMobile && !isTablet) {
                scrollContainerRef.current.scrollLeft = 0;
            }

            if (window.innerWidth > 1024) {
                setSlideWidth(
                    (Math.min(window.innerWidth, 1092) - 80) /
                        (props.small ? 4 : 3),
                );
            } else if (window.innerWidth > 600) {
                setSlideWidth(props.small ? 252 : 338);
            } else {
                setSlideWidth(props.small ? 252 : window.innerWidth - 50);
            }
        }
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;

        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleContainerScroll);
        }

        return () => {
            scrollContainer &&
                scrollContainer.removeEventListener(
                    'scroll',
                    handleContainerScroll,
                );
        };

        // eslint-disable-next-line
    }, [scrollContainerRef.current, screenWidth]);

    const recalculateCarouselSizes = () => {
        const newColumnSize = (screenWidth - margin) / columnQuantity;

        const newSlideWidth = itemColumns * newColumnSize;

        const newGridWidth = newSlideWidth * items.length - margin / 2;

        setColumnSize(newColumnSize);
        setSlideWidth(newSlideWidth);
        setGridWidth(newGridWidth);
    };

    const handleContainerScroll = event => {
        const contentWidth = slideWidth;

        let scrollLeft = event.target.scrollLeft;

        const currSlide = Math.min(
            Math.floor(scrollLeft / contentWidth),
            items.length - 1,
        );
        setCurrentSlide(currSlide);
    };

    const handlePreviousSlide = () => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth;

        if (currentSlide > 0) {
            scrollContainerRef.current.scrollTo({
                left: (currentSlide - 1) * slideSize,
                behavior: 'smooth',
            });
            //setCurrentSlide(currentSlide - 1);
        }

        props.handleClickArrow && props.handleClickArrow('previous');
    };

    const handleNextSlide = () => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth;
        if (currentSlide < items.length) {
            scrollContainerRef.current.scrollTo({
                left: (currentSlide + 1) * slideSize,
                behavior: 'smooth',
            });
            //setCurrentSlide(currentSlide + 1);
        }

        props.handleClickArrow && props.handleClickArrow('next');
    };

    return (
        <Container small={props.small}>
            <Scroll
                className="scroll-container"
                innerRef={scrollContainerRef}
                vertical={false}
                width={screenWidth - 50}
                small={props.small}
            >
                <ChapterRow
                    chapters={items}
                    small={props.small}
                    indexed={props.indexed}
                />
            </Scroll>
            {items.length > 4 && (
                <LeftControls>
                    <ButtonPrev
                        disabled={currentSlide <= 0}
                        onClick={handlePreviousSlide}
                    >
                        <ArrowIcon title={t('controls.previous')} />
                    </ButtonPrev>
                </LeftControls>
            )}

            {items.length > 4 && (
                <RightControls>
                    <ButtonNext
                        disabled={currentSlide >= items.length - visibleSlides}
                        onClick={handleNextSlide}
                    >
                        <ArrowIcon title={t('controls.next')} />
                    </ButtonNext>
                </RightControls>
            )}
        </Container>
    );
}

const Container = styled.div`
    position: relative;
    width: 100%;
    height: ${props => (props.small ? 215 : 245)}px;
    ${webMediaQuery} {
        width: calc(100% - 80px);
    }
`;

const Scroll = styled(ScrollContainer)`
    position: relative;
    width: 100%;
    height: ${props => (props.small ? 215 : 245)}px;
`;

export default ChapterCarousel;
